import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { NavRow } from "./NavRow";
import { ProfileMenu } from "./ProfileMenu";
import { TailRows } from "./TailRows";
import { ADD_PATH, DESTINATIONS } from "./navItems";
import { RAIL_WIDTH } from "./layout";
import type { Destination } from "./routes";
import { PlusIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * Tablet shell nav: a 72px icon rail, always visible, with the primary Add at
 * the top, the four destinations (Catalog, Browse, Items, Photos), the tail
 * icons and the account avatar (spec §2; catalog-selector 02). Tooltips carry
 * the labels.
 */
export function NavRail({
  destination,
  onNavigate,
}: {
  destination: Destination | null;
  onNavigate: (path: string) => void;
}) {
  const t = useT();
  const demoMode = useDemoMode();
  return (
    <Box
      component="nav"
      aria-label={t("primaryNav")}
      data-testid="nav-rail"
      sx={{
        width: RAIL_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        overflowY: "auto",
      }}
    >
      {!demoMode && (
        <>
          <NavRow
            icon={PlusIcon}
            label={t("add")}
            orientation="horizontal"
            showLabel={false}
            tooltip
            testId="nav-add"
            onClick={() => onNavigate(ADD_PATH)}
          />
          <Divider flexItem />
        </>
      )}

      {DESTINATIONS.map(({ destination: itemDestination, path, labelKey, icon }) => (
        <NavRow
          key={itemDestination}
          icon={icon}
          label={t(labelKey)}
          orientation="horizontal"
          showLabel={false}
          tooltip
          selected={itemDestination === destination}
          testId={`nav-${itemDestination}`}
          onClick={() => onNavigate(path)}
        />
      ))}

      <Box sx={{ flex: 1 }} />
      <Divider flexItem />
      <TailRows showLabel={false} tooltip onNavigate={onNavigate} />
      <Divider flexItem />
      <Box sx={{ py: 0.5 }}>
        <ProfileMenu variant="avatar" />
      </Box>
    </Box>
  );
}
